import type { Memo } from '@/api/types';

/**
 * Composable universel pour gérer toutes les règles de transformation
 * Utilisé par useMappings pour appliquer les transformations de manière cohérente
 */
export function useTransformations() {
  
  /**
   * Applique une règle de transformation unique
   */
  function applySingleTransformationRule(value: any, rule: string, memo: Memo): any {
    const colonIndex = rule.indexOf(':');
    const transformName = colonIndex > 0 ? rule.substring(0, colonIndex) : rule;
    const params = colonIndex > 0 ? rule.substring(colonIndex + 1) : '';

    switch (transformName) {
      case 'trim':
        return typeof value === 'string' ? value.trim() : value;
        
      case 'to_upper':
        return typeof value === 'string' ? value.toUpperCase() : value;
        
      case 'to_lower':
        return typeof value === 'string' ? value.toLowerCase() : value;
        
      case 'capitalize':
        return typeof value === 'string' ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;
        
      case 'title_case':
        return typeof value === 'string' ? value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()) : value;
        
      case 'title_case_keys':
        return applyTitleCaseKeys(value);
        
      case 'hide_if_empty':
        return (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) ? null : value;
        
      case 'hide_if_null':
        return value === null ? null : value;
        
      case 'hide_if_zero':
        return value === 0 ? null : value;
        
      case 'hide_if_false':
        return value === false ? null : value;
        
      case 'show_if_true':
        return value === true ? value : null;
        
      case 'default': {
        const defaultValue = params || '';
        return (value === null || value === undefined || value === '') ? defaultValue : value;
      }
        
      case 'to_string':
        return value !== null && value !== undefined ? String(value) : value;
        
      case 'to_number': {
        const num = Number(value);
        return isNaN(num) ? value : num;
      }
        
      case 'to_boolean': {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
          const lower = value.toLowerCase();
          return lower === 'true' || lower === '1' || lower === 'yes' || lower === 'oui';
        }
        return Boolean(value);
      }
        
      case 'to_date':
        if (value instanceof Date) return value;
        if (typeof value === 'string' || typeof value === 'number') {
          const date = new Date(value);
          return isNaN(date.getTime()) ? value : date;
        }
        return value;
        
      case 'to_json':
        return typeof value === 'object' ? JSON.stringify(value) : value;
        
      case 'from_json':
        if (typeof value === 'string') {
          try {
            return JSON.parse(value);
          } catch {
            return value;
          }
        }
        return value;
        
      case 'extract_year':
        if (value instanceof Date) return value.getFullYear();
        if (typeof value === 'string' || typeof value === 'number') {
          const date = new Date(value);
          return isNaN(date.getTime()) ? value : date.getFullYear();
        }
        return value;
        
      case 'extract_month':
        if (value instanceof Date) return value.getMonth() + 1;
        if (typeof value === 'string' || typeof value === 'number') {
          const date = new Date(value);
          return isNaN(date.getTime()) ? value : date.getMonth() + 1;
        }
        return value;
        
      case 'extract_day':
        if (value instanceof Date) return value.getDate();
        if (typeof value === 'string' || typeof value === 'number') {
          const date = new Date(value);
          return isNaN(date.getTime()) ? value : date.getDate();
        }
        return value;
        
      case 'round': {
        const decimals = parseInt(params) || 0;
        const numValue = Number(value);
        return isNaN(numValue) ? value : Number(numValue.toFixed(decimals));
      }
        
      case 'truncate': {
        const maxLength = parseInt(params) || 50;
        return typeof value === 'string' && value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
      }
        
      case 'custom_js':
        if (typeof value === 'string' && params) {
          try {
            const safeEval = new Function('value', `return ${params}`);
            return safeEval(value);
          } catch {
            return value;
          }
        }
        return value;
        
      case 'map_value':
        if (params && typeof value !== 'undefined' && value !== null) {
          const mappings = params.split(',').map(mapping => {
            const [key, val] = mapping.split('=');
            return { key: key.trim(), value: val.trim() };
          });
          
          const stringValue = String(value);
          for (const mapping of mappings) {
            if (stringValue === mapping.key) {
              return mapping.value;
            }
          }
        }
        return value;
        
      case 'auto_hierarchy':
        return applyAutomaticSectionMapping(memo, params, '', '');
        
      case 'concat':
        return applyConcatTransformation(value, params, memo);
        
      case 'hide_if':
        return applyConditionalHide(value, params, memo);
        
      case 'show_if':
        return applyConditionalShow(value, params, memo);
        
      case 'format_date':
        return applyDateFormat(value, params);
        
      case 'date_only_if_empty':
        return applyDateOnlyIfEmpty(value, params, memo);
        
      case 'remove_spaces':
        return typeof value === 'string' ? value.replace(/\s/g, '') : value;
        
      case 'replace_spaces': {
        const replacement = params || '_';
        return typeof value === 'string' ? value.replace(/\s/g, replacement) : value;
      }
        
      default:
        return value;
    }
  }

  /**
   * Applique title_case aux clés des objets
   */
  function applyTitleCaseKeys(value: any): any {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const result: Record<string, any> = {};
      for (const [key, val] of Object.entries(value)) {
        const titleCaseKey = key.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
        result[titleCaseKey] = val;
      }
      return result;
    }
    return value;
  }

  /**
   * Applique la transformation de concaténation
   */
  function applyConcatTransformation(value: any, params: string, memo: Memo): any {
    if (!params) return value;
    
    try {
      const parts = params.split('+').map(part => part.trim());
      
      // Vérifier si on a des champs d'agents dans la concaténation
      const hasAgentFields = parts.some(part => 
        !part.startsWith('"') && !part.endsWith('"') && 
        part.includes('agents_entries.agent.')
      );
      
      if (hasAgentFields) {
        return memo.agents_entries?.map((entry, index) => 
          applyAgentConcatTransformation(memo, parts, index)
        ).filter(result => result !== '') || [];
      }
      
      // Concaténation normale
      const resultParts: string[] = [];
      
      for (const part of parts) {
        if (part.startsWith('"') && part.endsWith('"')) {
          const staticText = part.slice(1, -1);
          resultParts.push(staticText);
        } else {
          const fieldValue = extractFieldFromMemoForConcat(memo, part);
          resultParts.push(fieldValue !== null && fieldValue !== undefined ? String(fieldValue) : '');
        }
      }
      
      return resultParts.join('');
    } catch (error) {
      console.error('Erreur lors de la concaténation:', error);
      return value;
    }
  }

  /**
   * Applique la transformation de concaténation pour un agent spécifique
   */
  function applyAgentConcatTransformation(memo: Memo, parts: string[], agentIndex: number): string {
    if (!memo.agents_entries || !Array.isArray(memo.agents_entries) || agentIndex >= memo.agents_entries.length) {
      return '';
    }

    const entry = memo.agents_entries[agentIndex];
    const agent = entry.agent;
    if (!agent) return '';

    const resultParts: string[] = [];
    
    for (const part of parts) {
      if (part.startsWith('"') && part.endsWith('"')) {
        const staticText = part.slice(1, -1);
        resultParts.push(staticText);
      } else {
        const fieldValue = extractAgentFieldForConcat(agent, entry, part);
        resultParts.push(fieldValue !== null && fieldValue !== undefined ? String(fieldValue) : '');
      }
    }
    
    return resultParts.join('');
  }

  /**
   * Extrait un champ d'agent spécifique pour la concaténation
   */
  function extractAgentFieldForConcat(agent: any, entry: any, fieldPath: string): any {
    const cleanPath = fieldPath.replace(/^["']|["']$/g, '');
    const agentField = cleanPath.replace('agents_entries.agent.', '');
    
    switch (agentField) {
      case 'uuid':
        return agent.uuid;
      case 'last_name':
        return agent.last_name;
      case 'first_name':
        return agent.first_name;
      case 'is_legal_entity':
        return agent.is_legal_entity;
      case 'idref_id':
        return agent.idref_id;
      case 'group':
        return agent.group;
      case 'affiliation':
        return agent.affiliation;
      case 'affiliation_id':
        return agent.affiliation_id;
      case 'roleslist':
        return entry.roleslist;
      default:
        return null;
    }
  }

  /**
   * Extrait un champ spécifique du mémo pour la concaténation
   */
  function extractFieldFromMemoForConcat(memo: Memo, fieldPath: string): any {
    const cleanPath = fieldPath.replace(/^["']|["']$/g, '');
    
    let sourceEntity = 'memo';
    let sourceField = cleanPath;
    
    if (cleanPath.includes('.')) {
      const firstDot = cleanPath.indexOf('.');
      const possibleEntity = cleanPath.substring(0, firstDot);
      
      if (['memo', 'material_entity', 'digital_entity', 'location', 'project', 'section'].includes(possibleEntity)) {
        sourceEntity = possibleEntity;
        sourceField = cleanPath.substring(firstDot + 1);
      }
    }
    
    const result = extractUniversalFieldValue(memo, sourceEntity, sourceField);
    
    if (Array.isArray(result) && result.length > 0) {
      return result[0];
    }
    
    return result;
  }

  /**
   * Fonction universelle pour extraire n'importe quel champ
   */
  function extractUniversalFieldValue(memo: Memo, sourceEntity: string, sourceField: string): any {
    let cleanField = sourceField;
    if (sourceField.startsWith(`${sourceEntity}.`)) {
      cleanField = sourceField.substring(sourceEntity.length + 1);
    }

    switch (sourceEntity) {
      case 'memo':
        return extractMemoField(memo, cleanField);
      
      case 'material_entity':
        return extractEntityField(memo.material_entity, cleanField);
      
      case 'digital_entity':
        return extractEntityField(memo.digital_entity_output, cleanField);
      
      case 'location':
        return extractEntityField(memo.location, cleanField);
      
      case 'project':
        return extractEntityField(memo.project, cleanField);
      
      case 'section':
        return extractSectionField(memo, cleanField);
      
      default:
        return null;
    }
  }

  /**
   * Extrait un champ du mémo principal
   */
  function extractMemoField(memo: Memo, fieldPath: string): any {
    if (fieldPath.startsWith('agents_entries')) {
      return extractAgentsField(memo, fieldPath);
    }
    return fieldPath.split('.').reduce((obj, key) => (obj as any)?.[key], memo);
  }

  /**
   * Extrait un champ d'une entité
   */
  function extractEntityField(entity: any, fieldPath: string): any {
    if (!entity) return null;
    return fieldPath.split('.').reduce((obj, key) => (obj as any)?.[key], entity);
  }

  /**
   * Extrait un champ d'une section
   */
  function extractSectionField(memo: Memo, fieldName: string): any {
    const dataEntry = memo.data_entries?.find(entry => 
      entry.name && (
        entry.name.toLowerCase() === fieldName.toLowerCase() ||
        entry.name.toLowerCase().includes(fieldName.toLowerCase())
      )
    );
    return dataEntry?.value || null;
  }

  /**
   * Extrait les champs d'agents
   */
  function extractAgentsField(memo: Memo, fieldPath: string): any {
    if (!memo.agents_entries || !Array.isArray(memo.agents_entries)) {
      return null;
    }

    const pathParts = fieldPath.split('.');
    if (pathParts.length < 3) return null;

    const agentField = pathParts.slice(2).join('.');

    return memo.agents_entries.map(entry => {
      const agent = entry.agent;
      if (!agent) return null;

      switch (agentField) {
        case 'uuid':
          return agent.uuid;
        case 'last_name':
          return agent.last_name;
        case 'first_name':
          return agent.first_name;
        case 'is_legal_entity':
          return agent.is_legal_entity;
        case 'idref_id':
          return agent.idref_id;
        case 'group':
          return agent.group;
        case 'affiliation':
          return agent.affiliation;
        case 'affiliation_id':
          return agent.affiliation_id;
        case 'roleslist':
          return entry.roleslist;
        default:
          return null;
      }
    }).filter(value => value !== null && value !== undefined);
  }

  /**
   * Applique la règle date_only_if_empty
   */
  function applyDateOnlyIfEmpty(value: any, fieldName: string, memo: Memo): any {
    const checkField = extractFieldFromMemoForConcat(memo, fieldName);
    if (checkField === null || checkField === undefined || checkField === '') {
      return new Date().toISOString();
    }
    return value;
  }

  /**
   * Applique un mapping automatique de sections
   */
  function applyAutomaticSectionMapping(
    memo: Memo, 
    sectionPrefix: string, 
    destinationPath: string, 
    transformation?: string
  ): Record<string, any> {
    const result: Record<string, any> = {};
    
    if (!memo.data_entries || !Array.isArray(memo.data_entries)) {
      return result;
    }
    
    const relevantEntries = memo.data_entries.filter(entry => 
      entry.name && entry.name.startsWith(sectionPrefix)
    );
    
    for (const entry of relevantEntries) {
      if (!entry.name) continue;
      const relativePath = entry.name.substring(sectionPrefix.length + 1);
      if (!relativePath) continue;
      
      // Créer une structure hiérarchique au lieu d'un chemin plat
      const pathParts = relativePath.split('.');
      let current = result;
      
      // Naviguer dans la hiérarchie et appliquer title_case_keys à toutes les clés
      for (let i = 0; i < pathParts.length - 1; i++) {
        let part = pathParts[i];
        
        // Appliquer title_case_keys à toutes les clés de la hiérarchie
        if (transformation && transformation.includes('title_case_keys')) {
          part = part.replace(/\w\S*/g, (txt) => 
            txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
          );
        }
        
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      }
      
      // Assigner la valeur au dernier niveau
      const lastPart = pathParts[pathParts.length - 1];
      let finalPart = lastPart;
      
      // Appliquer title_case_keys au dernier niveau aussi
      if (transformation && transformation.includes('title_case_keys')) {
        finalPart = lastPart.replace(/\w\S*/g, (txt) => 
          txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
        );
      }
      
      current[finalPart] = entry.value;
    }
    
    return result;
  }

  /**
   * Applique une condition de masquage
   */
  function applyConditionalHide(value: any, condition: string, memo: Memo): any {
    if (evaluateCondition(condition, memo)) {
      return null;
    }
    return value;
  }

  /**
   * Applique une condition d'affichage
   */
  function applyConditionalShow(value: any, condition: string, memo: Memo): any {
    if (!evaluateCondition(condition, memo)) {
      return null;
    }
    return value;
  }

  /**
   * Évalue une condition sur le mémo
   */
  function evaluateCondition(condition: string, memo: Memo): boolean {
    const [fieldPath, operator, expectedValue] = condition.split(/([=!<>]+)/);
    const actualValue = extractFieldFromMemoForConcat(memo, fieldPath);
    
    switch (operator) {
      case '=':
        return String(actualValue) === expectedValue;
      case '!=':
        return String(actualValue) !== expectedValue;
      case '>':
        return Number(actualValue) > Number(expectedValue);
      case '<':
        return Number(actualValue) < Number(expectedValue);
      case '>=':
        return Number(actualValue) >= Number(expectedValue);
      case '<=':
        return Number(actualValue) <= Number(expectedValue);
      default:
        return false;
    }
  }

  /**
   * Applique le formatage de date
   */
  function applyDateFormat(value: any, format: string): any {
    if (!value) return value;
    
    try {
      const date = value instanceof Date ? value : new Date(value);
      if (isNaN(date.getTime())) return value;
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return format
        .replace('YYYY', String(year))
        .replace('MM', month)
        .replace('DD', day)
        .replace('HH', hours)
        .replace('mm', minutes);
    } catch {
      return value;
    }
  }

  /**
   * Applique les règles de transformation
   */
  function applyTransformationRules(value: any, transformationRule: string, memo: Memo): any {
    if (!transformationRule || !transformationRule.trim()) {
      return value;
    }

    const rules = transformationRule.split('|').map(rule => rule.trim()).filter(rule => rule.length > 0);
    let result = value;
    
    for (const rule of rules) {
      result = applySingleTransformationRule(result, rule, memo);
    }
    
    return result;
  }

  return {
    applyTransformationRules,
    applySingleTransformationRule,
    applyAutomaticSectionMapping,
    applyConcatTransformation,
    applyAgentConcatTransformation,
    extractUniversalFieldValue,
    extractAgentsField
  };
} 